/**
 * Form state helpers shared by <add-item-form>, hemming, and sale-policy.
 *
 * `toggleFormEnabled(form, status, variant?)` — drives ATC button state,
 * sticky-ATC parity, the `variant-is-unavailable` class, the
 * `form-loading` class, and waitlist-button rendering for a single form.
 * `status` is one of:
 *   - `true`         — variant available; enable ATC, hide waitlist
 *   - `false`        — variant unavailable; disable ATC, surface waitlist
 *                      (also marks the waitlist button if the variant is
 *                      already in the customer's local waitlist cache)
 *   - `'loading'`    — submission in flight; disable everything
 *   - `'reset'`      — submission complete; restore default state
 *   - `'awaiting'`   — no variant selected yet; default state
 *
 * `renderFormError(form, error|false)` — quick-add forms (forms inside a
 * `.js-product-card`) surface errors as flash notifications; PDP forms
 * use the inline `.js-form-errors` panel with WAAPI slide-down/up.
 *
 * Both functions are pure (no module-level state); pass the form element
 * in. Callers can be the form's own custom element, a sibling controller,
 * or a cross-module flow (hemming terms decline, sale-policy decline).
 *
 * Source path: theme/js/core/form-state.esm.js
 * Built name : ./form-state.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-06
 * Model: Claude Opus 4.7
 *
 * [FormState]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { slideDown, slideUp, stop as stopAnim } from './core-anim.js';
import { push as notifierPush } from './notifier.js';
import { isVariantOnWaitlist, markWaitlistButton, resetWaitlistButton } from './waitlist-cache.js';

const SOLD_OUT_CLASS = 'variant-is-unavailable';
const FORM_LOADING_CLASS = 'form-loading';
const HAS_VARIANT_CLASS = 'has-selected-variant';
const ADD_TO_CART_BUTTON_SELECTOR = '.js-add-to-cart';
const SOLD_OUT_BUTTON_SELECTOR = '.js-product-unavailable-button';
const WAITLIST_BUTTON_SELECTOR = '.js-waitlist-button';
const PRODUCT_SELECTOR = '.js-product';
const FORM_ERROR_SELECTOR = '.js-form-errors';
const FORM_ERROR_MESSAGE_SELECTOR = '.js-form-error-message';

function getStickyAtcSelector() {
  return appGet('dom.product.stickyAtcSelector', '.js-sticky-add-to-cart');
}

function getCardSelector() {
  return appGet('dom.product.cardSelector', '.js-product-card:not(.grid-tout)');
}

function isQuickAddForm(form) {
  if (appGet('page_type') === 'product') return false;
  return !!form.closest(getCardSelector());
}

function getStickyAtc(form) {
  const product = form.closest(PRODUCT_SELECTOR);
  if (!product) return null;
  return product.querySelector(getStickyAtcSelector());
}

export function toggleFormEnabled(form, status, variant) {
  if (!form) return;

  const addBtn = form.querySelector(ADD_TO_CART_BUTTON_SELECTOR);
  const soldOutBtn = form.querySelector(SOLD_OUT_BUTTON_SELECTOR);
  const stickyAtc = isQuickAddForm(form) ? null : getStickyAtc(form);
  const stickyAddBtn = stickyAtc ? stickyAtc.querySelector(ADD_TO_CART_BUTTON_SELECTOR) : null;
  const stickySoldBtn = stickyAtc ? stickyAtc.querySelector(SOLD_OUT_BUTTON_SELECTOR) : null;
  const productCard = isQuickAddForm(form) ? form.closest(getCardSelector()) : null;

  if (status === true && variant) {
    log('[Form] variant selected: ' + variant.title);
  } else if (status === false && variant) {
    log('[Form] variant unavailable: ' + variant.title);
  } else if (status === 'loading') {
    log('[Form] submitting');
  } else if (status === 'reset') {
    log('[Form] submitted');
  } else if (status === 'awaiting') {
    log('[Form] awaiting selection');
  }

  if (status === true) {
    form.classList.remove(FORM_LOADING_CLASS, SOLD_OUT_CLASS);
    if (productCard) productCard.classList.remove(SOLD_OUT_CLASS);
    if (addBtn) addBtn.disabled = false;
    if (soldOutBtn) soldOutBtn.disabled = true;
    if (stickyAtc) {
      stickyAtc.classList.remove(FORM_LOADING_CLASS, SOLD_OUT_CLASS);
      if (stickySoldBtn) stickySoldBtn.disabled = true;
    }
  } else if (status === 'loading') {
    form.classList.add(FORM_LOADING_CLASS);
    form.classList.remove(SOLD_OUT_CLASS);
    if (productCard) productCard.classList.remove(SOLD_OUT_CLASS);
    if (addBtn) addBtn.disabled = true;
    if (soldOutBtn) soldOutBtn.disabled = true;
    if (stickyAtc) {
      stickyAtc.classList.add(FORM_LOADING_CLASS);
      stickyAtc.classList.remove(SOLD_OUT_CLASS);
      if (stickyAddBtn) stickyAddBtn.disabled = true;
      if (stickySoldBtn) stickySoldBtn.disabled = true;
    }
  } else if (status === 'reset' || status === 'awaiting') {
    form.classList.remove(FORM_LOADING_CLASS, SOLD_OUT_CLASS);
    if (productCard) productCard.classList.remove(SOLD_OUT_CLASS);
    if (addBtn) addBtn.disabled = false;
    if (stickyAtc) {
      stickyAtc.classList.remove(FORM_LOADING_CLASS, SOLD_OUT_CLASS);
      if (stickyAddBtn) stickyAddBtn.disabled = false;
      if (stickySoldBtn) stickySoldBtn.disabled = true;
    }
  } else if (status === false) {
    form.classList.remove(FORM_LOADING_CLASS);
    form.classList.add(SOLD_OUT_CLASS);
    if (productCard) productCard.classList.add(SOLD_OUT_CLASS);
    if (addBtn) addBtn.disabled = true;
    if (soldOutBtn) soldOutBtn.disabled = false;
    if (stickyAtc) {
      stickyAtc.classList.remove(FORM_LOADING_CLASS);
      stickyAtc.classList.add(SOLD_OUT_CLASS);
      if (stickyAddBtn) stickyAddBtn.disabled = true;
      if (stickySoldBtn) stickySoldBtn.disabled = false;
    }
    if (variant) {
      const waitlistBtn = form.querySelector(WAITLIST_BUTTON_SELECTOR);
      if (waitlistBtn) {
        resetWaitlistButton(waitlistBtn);
        if (isVariantOnWaitlist(variant.id)) {
          markWaitlistButton(waitlistBtn);
        }
      }
    }
  }
}

export function renderFormError(form, error) {
  if (!form) return;

  if (isQuickAddForm(form)) {
    if (error !== false) {
      notifierPush({
        type: 'flash',
        content: error,
        elementType: 'error',
        force: true,
      });
      const ga = appGet('ga');
      if (typeof window.app?.ga === 'function') {
        window.app.ga('send', 'event', {
          eventCategory: 'FlashMessage',
          eventAction: 'Shown',
          eventLabel: 'Quick Add - Error',
        });
      }
    }
    return;
  }

  const formError = form.querySelector(FORM_ERROR_SELECTOR);
  const formErrorMessage = form.querySelector(FORM_ERROR_MESSAGE_SELECTOR);
  if (!formError || !formErrorMessage) return;
  const animProps = appGet('animations.productError');

  if (error !== false) {
    formErrorMessage.textContent = error;
    stopAnim(formError);
    slideDown(formError, { duration: animProps?.in?.duration ?? 125 });
  } else {
    stopAnim(formError);
    slideUp(formError, { duration: animProps?.out?.duration ?? 125 }).then(() => {
      formErrorMessage.textContent = '';
    });
  }
}

export { HAS_VARIANT_CLASS, FORM_LOADING_CLASS, SOLD_OUT_CLASS };

log('[FormState] registered');
